import { toTitleCase, isNullOrEmpty } from "@jcharlesworthuk/your-mum-core/dist/functions";

/**
 * Works with the <SocialButton /> component among others
 */
export interface ISocialPage {
  name: typeof SocialMediaAccountName[number];
  readonly pageUrl: string;
  /** The profile handle in a nice to display format (eg @MyTwitter) */
  readonly displayHandle: string;
  /** The social media site name in a nice to display format (eg Youtube Channel) */
  readonly displaySiteName: string;
  /** Some text encouraging people to follow you the person (eg. Subscribe to my Youtube Channel) */
  readonly displayFollowMe: string;
}

export const SocialMediaAccountName = <const>["Facebook-Page", "Twitter", "Instagram", "TikTok", "Youtube-Channel", "LinkedIn-Company", "LinkedIn-Profile", "Pintrest"];
export type SocialMediaAccounts = {
  [key in typeof SocialMediaAccountName[number]]?: ISocialPage
}

export type SocialMediaAccountsDto = {
  [key in typeof SocialMediaAccountName[number]]?: string | object
}

export function mapSocialMediaAccountsDto(dto: SocialMediaAccountsDto): SocialMediaAccounts {
  const result: SocialMediaAccounts = {};
  for (let key in dto) {
    switch (key as typeof SocialMediaAccountName[number]) {
      case "Facebook-Page":
        const fbKeySects = <string>dto[key].split('~');
        result[key] = new FacebookPage(fbKeySects[0], fbKeySects[1]);
        break;
      case "Twitter":
        result[key] = new TwitterPage(<string>dto[key]);
        break;
      case "LinkedIn-Profile":
        result[key] = new LinkedInProfile(<string>dto[key]);
        break;
      case "LinkedIn-Company":
        result[key] = new LinkedInCompany(<string>dto[key]);
        break;
      case "Youtube-Channel":
        result[key] = new YoutubeChannel(<string>dto[key]);
        break;
      case "Instagram":
        result[key] = new InstagramPage(<string>dto[key]);
        break;
      case "TikTok":
        result[key] = new TikTokPage(<string>dto[key]);
        break;
      case "Pintrest":
        result[key] = new PintrestPage(<string>dto[key]);
        break;
      default: throw new Error('Unrecognised social site: ' + key);
    }
  }
  return result;
}

function stripAtSign(handle: string) {
  if (handle.startsWith('@')) return handle.substring(1);
  return handle.trim();
}


export class FacebookPage implements ISocialPage {
  constructor(private handle: string, private facebookPageId: string) {
  }
  name: typeof SocialMediaAccountName[number] = "Facebook-Page";
  
  get pageUrl() {
    return isNullOrEmpty(this.facebookPageId)
      ? `https://www.facebook.com/pg/${stripAtSign(this.handle)}`
      : `https://www.facebook.com/${stripAtSign(this.handle)}-${this.facebookPageId}/about`
  }
  get displayHandle() {
    return toTitleCase(this.handle.replace(/[\._-]/g, ' '));
  }
  get displaySiteName() {
    return `Facebook Page`
  }
  get displayFollowMe() {
    return `Like my Facebook page`
  }
}

export class TwitterPage implements ISocialPage {
  constructor(private handle: string) {
  }
  name: typeof SocialMediaAccountName[number] = "Twitter";
  get pageUrl() {
    return `https://twitter.com/${stripAtSign(this.handle)}`;
  }
  get displayHandle() {
    return `@${stripAtSign(this.handle)}`;
  }
  get displaySiteName() {
    return `Twitter`
  }
  get displayFollowMe() {
    return `Follow me on Twitter`
  }
}

export class LinkedInProfile implements ISocialPage {
  constructor(private handle: string) {
  }
  name: typeof SocialMediaAccountName[number] = "LinkedIn-Profile";
  get pageUrl() {
    return `https://www.linkedin.com/${stripAtSign(this.handle)}`;
  }
  get displayHandle() {
    return stripAtSign(this.handle);
  }
  get displaySiteName() {
    return `LinkedIn`
  }
  get displayFollowMe() {
    return `Connect on LinkedIn`
  }
}

export class LinkedInCompany implements ISocialPage {
  constructor(private handle: string) {
  }
  name: typeof SocialMediaAccountName[number] = "LinkedIn-Company";
  get pageUrl() {
    return `https://www.linkedin.com/company/${this.handle}`;
  }
  get displayHandle() {
    return this.handle;
  }
  get displaySiteName() {
    return `LinkedIn`
  }
  get displayFollowMe() {
    return `Follow us on LinkedIn`
  }
}


export class YoutubeChannel implements ISocialPage {
  constructor(private channelId: string) {
  }
  name: typeof SocialMediaAccountName[number] = "Youtube-Channel";
  get pageUrl() {
    return `https://www.youtube.com/channel/${this.channelId}`;
  }
  get displayHandle() {
    return `My Channel`;
  }
  get displaySiteName() {
    return `Youtube Channel`
  }
  get displayFollowMe() {
    return `Follow me on Twitter`
  }
}

export class InstagramPage implements ISocialPage {
  constructor(private handle: string) {
  }
  name: typeof SocialMediaAccountName[number] = "Instagram";
  get pageUrl() {
    return `https://www.instagram.com/${stripAtSign(this.handle)}`;
  }
  get displayHandle() {
    return `@${stripAtSign(this.handle)}`;
  }
  get displaySiteName() {
    return `Instagram`
  }
  get displayFollowMe() {
    return `Follow me on Instagram`
  }

}


export class TikTokPage implements ISocialPage {
  constructor(private handle: string) {
  }
  name: typeof SocialMediaAccountName[number] = "TikTok";
  get pageUrl() {
    return `https://www.tiktok.com/@${stripAtSign(this.handle)}`;
  }
  get displayHandle() {
    return `@${stripAtSign(this.handle)}`;
  }
  get displaySiteName() {
    return `TikTok`
  }
  get displayFollowMe() {
    return `Follow me on TikTok`
  }

}

export class PintrestPage implements ISocialPage {
  constructor(private handle: string) {
  }
  name: typeof SocialMediaAccountName[number] = "Pintrest";
  get pageUrl() {
    return `https://www.pintrest.com/@${stripAtSign(this.handle)}`;
  }
  get displayHandle() {
    return `@${stripAtSign(this.handle)}`;
  }
  get displaySiteName() {
    return `Pintrest`
  }
  get displayFollowMe() {
    return `Follow me on Pintrest`
  }

}
